import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { matchesAPI, announcementsAPI, sportsAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Search, Calendar, Users, MapPin, Megaphone, Plus, Clock, X
} from 'lucide-react';
import {
  formatTime, getRelativeTime, getStatusColorClass,
  getStatusLabel, getInitials, ANNOUNCEMENT_TYPES
} from '@/lib/utils';

const TabPill = ({ active, label, icon: Icon, count, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all ${
      active
        ? 'bg-white text-foreground shadow-md'
        : 'bg-white/15 text-white hover:bg-white/25 backdrop-blur'
    }`}
  >
    <Icon className="h-4 w-4" />
    <span>{label}</span>
    <span className={`text-[11px] px-2 py-0.5 rounded-full tabular-nums ${
      active ? 'bg-primary/15 text-primary' : 'bg-white/25'
    }`}>
      {count}
    </span>
  </button>
);

const MatchRow = ({ match }) => (
  <Link to={`/matches/${match.match_id}`} className="block group">
    <Card className="overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all">
      <CardContent className="p-0">
        <div className="flex">
          {/* Date column */}
          <div className="w-20 md:w-24 shrink-0 bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex flex-col items-center justify-center py-4 px-2">
            <div className="text-[10px] uppercase tracking-widest opacity-80">
              {new Date(match.date_time).toLocaleDateString('it-IT', { weekday: 'short' })}
            </div>
            <div className="text-2xl font-bold tabular-nums leading-none mt-0.5">
              {new Date(match.date_time).getDate()}
            </div>
            <div className="text-[10px] uppercase tracking-widest opacity-80 mt-0.5">
              {new Date(match.date_time).toLocaleDateString('it-IT', { month: 'short' })}
            </div>
            <div className="mt-2 px-2 py-0.5 rounded-full bg-white/20 text-[10px] tabular-nums">
              {formatTime(match.date_time)}
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 p-4 min-w-0">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex items-center gap-1.5 flex-wrap">
                <Badge variant="secondary" className="text-[11px]">{match.sport_name}</Badge>
                <Badge className={`text-[11px] ${getStatusColorClass(match.status)}`}>
                  {getStatusLabel(match.status)}
                </Badge>
              </div>
              <span className="text-xs text-muted-foreground shrink-0 hidden md:inline">
                {getRelativeTime(match.date_time)}
              </span>
            </div>
            <h3 className="font-bold text-lg leading-tight group-hover:text-primary transition-colors truncate">
              {match.title}
            </h3>
            {match.league_name && (
              <p className="text-xs text-muted-foreground mt-0.5">{match.league_name}</p>
            )}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5" />
                {match.location_name}
              </span>
              <span className="flex items-center gap-1">
                <Users className="h-3.5 w-3.5" />
                {match.participants_count || 0}/{match.max_participants}
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  </Link>
);

const AnnouncementMini = ({ ann }) => {
  const type = ANNOUNCEMENT_TYPES.find(t => t.value === ann.type);
  return (
    <Card className="overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <Avatar className="h-10 w-10 shrink-0 ring-2 ring-background">
            <AvatarImage src={ann.user?.picture} />
            <AvatarFallback>{getInitials(ann.user?.name)}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 mb-1">
              <p className="font-medium truncate text-sm">{ann.user?.name}</p>
              <Badge variant="outline" className="text-[10px] shrink-0">
                {type?.label || ann.type}
              </Badge>
            </div>
            <h4 className="font-semibold leading-tight">{ann.title}</h4>
            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
              {ann.content}
            </p>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-3 text-[11px] text-muted-foreground">
              {ann.sport_name && (
                <Badge variant="secondary" className="text-[10px]">{ann.sport_name}</Badge>
              )}
              {ann.city && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {ann.city}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {getRelativeTime(ann.created_at)}
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [matches, setMatches] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [sports, setSports] = useState([]);

  const [filters, setFilters] = useState({
    query: searchParams.get('q') || '',
    sport: searchParams.get('sport') || 'all',
    city: searchParams.get('city') || '',
    type: searchParams.get('type') || 'all',
  });

  const activeTab = searchParams.get('tab') || 'matches';

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [matchesRes, annRes, sportsRes] = await Promise.all([
          matchesAPI.getAll({
            is_public: true,
            sport_id: filters.sport !== 'all' ? filters.sport : undefined,
            city: filters.city || undefined,
          }),
          announcementsAPI.getAll({
            sport_id: filters.sport !== 'all' ? filters.sport : undefined,
            city: filters.city || undefined,
            type: filters.type !== 'all' ? filters.type : undefined,
          }),
          sportsAPI.getAll(),
        ]);

        const upcoming = matchesRes.data.filter(m =>
          new Date(m.date_time) >= new Date() && m.status !== 'cancelled'
        );

        setMatches(upcoming);
        setAnnouncements(annRes.data);
        setSports(sportsRes.data);
      } catch (error) {
        console.error('Error searching:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [filters.sport, filters.city, filters.type]);

  const handleTabChange = (tab) => {
    setSearchParams(prev => {
      prev.set('tab', tab);
      return prev;
    });
  };

  const filteredMatches = matches.filter(match => {
    if (!filters.query) return true;
    const q = filters.query.toLowerCase();
    return match.title.toLowerCase().includes(q) ||
      match.location_name.toLowerCase().includes(q) ||
      match.league_name?.toLowerCase().includes(q);
  });

  const filteredAnnouncements = announcements.filter(ann => {
    if (!filters.query) return true;
    const q = filters.query.toLowerCase();
    return ann.title.toLowerCase().includes(q) ||
      ann.content.toLowerCase().includes(q);
  });

  const hasActiveFilters = filters.sport !== 'all' || filters.city || (activeTab === 'announcements' && filters.type !== 'all');

  return (
    <div className="pb-24" data-testid="search-page">
      {/* Hero with prominent search */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600" />
        <div className="pointer-events-none absolute -top-32 -right-32 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-white/15 blur-3xl" />
        <div className="relative px-4 md:px-8 pt-8 pb-12 md:pt-12 md:pb-16">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-6">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/20 backdrop-blur text-white text-[11px] uppercase tracking-widest font-semibold mb-3">
                <Search className="h-3 w-3" />
                Esplora
              </span>
              <h1 className="text-3xl md:text-5xl font-bold text-white drop-shadow-lg">
                Cosa stai cercando?
              </h1>
              <p className="text-white/85 mt-2">
                Partite vicine a te, annunci, compagni di squadra. Tutto in un posto.
              </p>
            </div>

            {/* Big search */}
            <div className="relative">
              <div className="absolute inset-0 bg-white/30 rounded-2xl blur-xl" aria-hidden />
              <div className="relative bg-white dark:bg-slate-900 rounded-2xl shadow-2xl flex items-center pr-2">
                <Search className="ml-5 h-6 w-6 text-muted-foreground shrink-0" />
                <Input
                  placeholder="Cerca partite, luoghi, leghe, annunci..."
                  value={filters.query}
                  onChange={(e) => setFilters({ ...filters, query: e.target.value })}
                  className="border-0 h-14 md:h-16 text-base md:text-lg shadow-none focus-visible:ring-0 bg-transparent"
                  data-testid="search-input"
                />
                {filters.query && (
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-9 w-9 shrink-0"
                    onClick={() => setFilters({ ...filters, query: '' })}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>

            {/* Tabs in hero */}
            <div className="flex justify-center gap-2 mt-6">
              <TabPill
                active={activeTab === 'matches'}
                label="Partite"
                icon={Calendar}
                count={filteredMatches.length}
                onClick={() => handleTabChange('matches')}
              />
              <TabPill
                active={activeTab === 'announcements'}
                label="Annunci"
                icon={Megaphone}
                count={filteredAnnouncements.length}
                onClick={() => handleTabChange('announcements')}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-8 -mt-6 space-y-6">
        {/* Filters bar */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-3">
              <Select
                value={filters.sport}
                onValueChange={(value) => setFilters({ ...filters, sport: value })}
              >
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Sport" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti gli sport</SelectItem>
                  {sports.map(sport => (
                    <SelectItem key={sport.sport_id} value={sport.sport_id}>
                      {sport.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="relative w-[180px]">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Città"
                  value={filters.city}
                  onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                  className="pl-9"
                />
              </div>

              {activeTab === 'announcements' && (
                <Select
                  value={filters.type}
                  onValueChange={(value) => setFilters({ ...filters, type: value })}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti i tipi</SelectItem>
                    {ANNOUNCEMENT_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-auto text-muted-foreground"
                  onClick={() => setFilters({ ...filters, sport: 'all', city: '', type: 'all' })}
                >
                  <X className="h-3.5 w-3.5 mr-1" />
                  Pulisci filtri
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {activeTab === 'matches' && (
          <>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => <Skeleton key={i} className="h-28" />)}
              </div>
            ) : filteredMatches.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <div className="h-14 w-14 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white shadow-lg">
                    <Calendar className="h-7 w-7" />
                  </div>
                  <h2 className="text-xl font-semibold mb-1">Nessuna partita trovata</h2>
                  <p className="text-muted-foreground mb-5">
                    Prova a rimuovere qualche filtro o crea tu una partita pubblica.
                  </p>
                  <Link to="/matches/new">
                    <Button>
                      <Plus className="h-4 w-4 mr-2" />
                      Crea una partita
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredMatches.map(match => (
                  <MatchRow key={match.match_id} match={match} />
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'announcements' && (
          <>
            <div className="flex justify-end">
              <Link to="/announcements/new">
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Crea Annuncio
                </Button>
              </Link>
            </div>
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-40" />)}
              </div>
            ) : filteredAnnouncements.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <div className="h-14 w-14 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center text-white shadow-lg">
                    <Megaphone className="h-7 w-7" />
                  </div>
                  <h2 className="text-xl font-semibold mb-1">Nessun annuncio trovato</h2>
                  <p className="text-muted-foreground mb-5">
                    Pubblica il primo annuncio in questa area.
                  </p>
                  <Link to="/announcements/new">
                    <Button>
                      <Plus className="h-4 w-4 mr-2" />
                      Crea Annuncio
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredAnnouncements.map(ann => (
                  <AnnouncementMini key={ann.announcement_id} ann={ann} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SearchPage;
